// Pattern: docs/design-patterns/frontend-accessibility
// Real-time password requirements checklist — reads from centralized policy config

import type { PasswordPolicy } from '../config/password-policy';
import { passwordPolicy } from '../config/password-policy';

interface PasswordRequirementsProps {
  password: string;
  policy?: PasswordPolicy;
}

interface RequirementItem {
  label: string;
  met: boolean;
}

function getRequirements(password: string, policy: PasswordPolicy): RequirementItem[] {
  const items: RequirementItem[] = [
    { label: `At least ${policy.minLength} characters`, met: password.length >= policy.minLength },
  ];

  if (policy.requireUppercase) {
    items.push({ label: 'One uppercase letter', met: /[A-Z]/.test(password) });
  }
  if (policy.requireLowercase) {
    items.push({ label: 'One lowercase letter', met: /[a-z]/.test(password) });
  }
  if (policy.requireNumber) {
    items.push({ label: 'One number', met: /\d/.test(password) });
  }
  if (policy.requireSymbol) {
    items.push({ label: 'One special character', met: /[^A-Za-z0-9]/.test(password) });
  }

  return items;
}

export function PasswordRequirements({
  password,
  policy = passwordPolicy,
}: PasswordRequirementsProps): React.JSX.Element {
  const requirements = getRequirements(password, policy);
  const allMet = requirements.every(r => r.met);

  return (
    <div
      aria-label="Password requirements"
      role="list"
      className={`mt-3 rounded-lg border px-4 py-3 transition-colors ${
        allMet ? 'border-success/40 bg-success/5' : 'border-default-200 bg-default-50'
      }`}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
        Password must contain
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {requirements.map(req => (
          <div
            key={req.label}
            role="listitem"
            aria-label={`${req.label}: ${req.met ? 'met' : 'not met'}`}
            className={`flex items-center gap-1.5 text-xs transition-colors ${
              req.met ? 'text-success' : 'text-foreground-400'
            }`}
          >
            <span aria-hidden="true" className="flex-shrink-0">
              {req.met ? '\u2713' : '\u25CB'}
            </span>
            <span>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
